import { useState, useRef, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useTonWallet, useTonConnectUI } from '@tonconnect/ui-react'
import { Loader2 } from 'lucide-react'
import type { AppContext } from './layout/AppLayout'
import { BottomSheet, Input } from './ui'
import { api } from '../lib/api'
import { haptics } from '../lib/haptics'

interface Props {
  open: boolean
  onClose: () => void
  /** Called after successful creation with the new key secret */
  onSuccess?: (key: string) => void
}

export default function CreateKeySheet({ open, onClose, onSuccess }: Props) {
  const { balance, refreshBalance, setFirstKey } = useOutletContext<AppContext>()
  const wallet = useTonWallet()
  const [tonConnectUI] = useTonConnectUI()
  const address = wallet?.account.address

  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input after sheet animation completes (~350ms spring)
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => inputRef.current?.focus(), 380)
    return () => clearTimeout(t)
  }, [open])

  const isFirstKey =
    (balance?.keys ?? []).length === 0 &&
    !localStorage.getItem(`imago:key:${address ?? ''}`)

  const handleClose = () => {
    setName('')
    setError(null)
    onClose()
  }

  const handleCreate = async () => {
    if (!address || !name.trim()) return
    setCreating(true)
    setError(null)
    try {
      const result = await api.createKey(address, name.trim(), '')
      localStorage.setItem(`imago:key:${address}:${result.id}`, result.key)
      haptics.notification('success')
      if (isFirstKey) setFirstKey(address, result.key)
      refreshBalance()
      onSuccess?.(result.key)
      handleClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create key')
      haptics.notification('error')
    } finally {
      setCreating(false)
    }
  }

  // Guard: if sheet opened without wallet — show connect CTA
  if (!wallet) {
    return (
      <BottomSheet open={open} onClose={handleClose} title="New API Key">
        <div className="px-sp-4 pb-sp-6 flex flex-col items-center gap-sp-4 text-center">
          <p className="text-[14px]" style={{ color: 'var(--tg-theme-hint-color)' }}>
            Connect your wallet to create an API key
          </p>
          <button
            onClick={() => { haptics.impact('medium'); tonConnectUI.openModal() }}
            className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity flex items-center justify-center"
            style={{ backgroundColor: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
          >
            Connect Wallet
          </button>
        </div>
      </BottomSheet>
    )
  }

  return (
    <BottomSheet open={open} onClose={handleClose} title="New API Key">
      <div className="px-sp-4 pb-sp-6 flex flex-col gap-sp-4">
        <Input
          ref={inputRef}
          placeholder='e.g. "My project"'
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) handleCreate() }}
        />

        {error && (
          <p className="text-[13px] text-center" style={{ color: 'var(--tg-theme-destructive-text-color)' }}>
            {error}
          </p>
        )}

        <button
          onClick={() => { haptics.impact('medium'); handleCreate() }}
          disabled={!name.trim() || creating}
          className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity disabled:opacity-40 flex items-center justify-center gap-sp-2"
          style={{ backgroundColor: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
        >
          {creating ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Creating…
            </>
          ) : 'Create Key'}
        </button>
      </div>
    </BottomSheet>
  )
}
